import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  BarChart3, Filter, Calendar, Users, UserCircle, ChevronLeft, ChevronRight,
  AlertTriangle, CheckCircle, Clock, TrendingUp, TrendingDown, Loader2, RefreshCw,
  DollarSign
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { PageErrorBoundary, WidgetErrorBoundary } from '../components/ErrorBoundary';
import { SkeletonCard } from '../components/Skeleton';
import EmptyState from '../components/EmptyState';
import BackButton from '../components/BackButton';

// ==========================================
// CURRENCY OPTIONS (Display Only)
// ==========================================
const DISPLAY_CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
];

// ==========================================
// QUICK CURRENCY SWITCH COMPONENT
// ==========================================
const QuickCurrencySwitch = ({ currency, onCurrencyChange }) => (
  <div className="flex items-center gap-2">
    <DollarSign className="w-4 h-4 text-gray-400" />
    <select
      value={currency}
      onChange={(e) => onCurrencyChange(e.target.value)}
      className="text-sm border rounded-lg px-2 py-1 bg-white focus:ring-2 focus:ring-purple-500"
      data-testid="currency-switch"
    >
      {DISPLAY_CURRENCIES.map(c => (
        <option key={c.code} value={c.code}>{c.code} ({c.symbol})</option>
      ))}
    </select>
  </div>
);

// ==========================================
// CONFLICT BADGE FOR ROW
// ==========================================
const RowConflictBadge = ({ conflicts, entityId, entityType }) => {
  const entityConflicts = useMemo(() => {
    return conflicts.filter(c => c.entity_id === entityId && c.entity_type === entityType);
  }, [conflicts, entityId, entityType]);
  
  if (entityConflicts.length === 0) return null;
  
  const totalOverHours = entityConflicts.reduce((sum, c) => sum + c.over_by_hours, 0);
  
  return (
    <span 
      className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-700"
      title={`Overallocated by +${totalOverHours.toFixed(0)}h across ${entityConflicts.length} month(s)`}
      data-testid={`row-conflict-badge-${entityId}`}
    >
      <AlertTriangle className="w-2.5 h-2.5" />
      {entityConflicts.length}
    </span>
  );
};

// ==========================================
// HEATMAP CELL COMPONENT
// ==========================================

const HeatmapCell = ({ data, month }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const getColor = (status) => {
    switch (status) {
      case 'over': return 'bg-red-500';
      case 'high': return 'bg-amber-400';
      case 'normal': return 'bg-blue-400';
      case 'under': return 'bg-green-400';
      default: return 'bg-gray-200';
    }
  };

  const getTextColor = (status) => {
    switch (status) {
      case 'over': return 'text-white';
      case 'high': return 'text-amber-900';
      case 'normal': return 'text-blue-900';
      case 'under': return 'text-green-900';
      default: return 'text-gray-600';
    }
  };

  if (!data) {
    return <div className="w-full h-12 bg-gray-100 rounded" />;
  }

  return (
    <div 
      className="relative"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <div 
        className={`w-full h-12 rounded flex items-center justify-center cursor-pointer transition-transform hover:scale-105 ${getColor(data.status)}`}
        data-testid={`heatmap-cell-${month}`}
      >
        <span className={`text-sm font-semibold ${getTextColor(data.status)}`}>
          {data.utilisation}%
        </span>
      </div>
      
      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-gray-900 text-white rounded-lg shadow-xl p-3 text-xs">
          <div className="font-semibold mb-2">{month}</div>
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-gray-400">Capacity:</span>
              <span>{data.capacity?.toFixed(0) || 0}h</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Allocated:</span>
              <span>{data.allocated?.toFixed(0) || 0}h</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Remaining:</span>
              <span className={data.remaining < 0 ? 'text-red-400' : 'text-green-400'}>
                {data.remaining?.toFixed(0) || 0}h
              </span>
            </div>
          </div>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
        </div>
      )}
    </div>
  );
};

// ==========================================
// HEATMAP GRID COMPONENT
// ==========================================

const HeatmapGrid = ({ data, months, conflicts = [] }) => {
  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={BarChart3}
        title="No utilisation data"
        description="Configure roles and teams, then create allocations to see utilisation."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[800px]">
        <thead>
          <tr>
            <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase w-56 sticky left-0 bg-white z-10">
              Resource
            </th>
            {months.map(month => (
              <th key={month} className="py-3 px-2 text-xs font-semibold text-gray-600 text-center">
                {new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map(entity => (
            <tr key={entity.id} className="border-t" data-testid={`heatmap-row-${entity.id}`}>
              <td className="py-2 px-4 sticky left-0 bg-white z-10">
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    entity.type === 'role' ? 'bg-purple-100' : 'bg-blue-100'
                  }`}>
                    {entity.type === 'role' 
                      ? <UserCircle className="w-4 h-4 text-purple-600" />
                      : <Users className="w-4 h-4 text-blue-600" />
                    }
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center">
                      <p className="font-medium text-sm text-gray-900 truncate">{entity.name}</p>
                      <RowConflictBadge conflicts={conflicts} entityId={entity.id} entityType={entity.type} />
                    </div>
                    <p className="text-xs text-gray-500 capitalize">{entity.type}</p>
                  </div>
                </div>
              </td>
              {months.map(month => (
                <td key={month} className="py-2 px-2">
                  <HeatmapCell data={entity.months?.[month]} month={month} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ==========================================
// SUMMARY STATS COMPONENT
// ==========================================

const SummaryStats = ({ data, months }) => {
  const stats = useMemo(() => {
    if (!data || data.length === 0) {
      return { overCount: 0, normalCount: 0, underCount: 0, avgUtilisation: 0 };
    }

    let overCount = 0;
    let normalCount = 0;
    let underCount = 0;
    let totalUtilisation = 0;
    let count = 0;

    data.forEach(entity => {
      Object.values(entity.months || {}).forEach(monthData => {
        if (monthData.status === 'over') overCount++;
        else if (monthData.status === 'high' || monthData.status === 'normal') normalCount++;
        else underCount++;
        totalUtilisation += monthData.utilisation || 0;
        count++;
      });
    });

    return {
      overCount,
      normalCount,
      underCount,
      avgUtilisation: count > 0 ? (totalUtilisation / count).toFixed(1) : 0
    };
  }, [data]);

  return (
    <div className="grid grid-cols-4 gap-4 mb-6">
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-100 rounded-xl">
            <BarChart3 className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Avg Utilisation</p>
            <p className="text-2xl font-bold text-gray-900">{stats.avgUtilisation}%</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-red-100 rounded-xl">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Over Capacity</p>
            <p className="text-2xl font-bold text-red-600">{stats.overCount}</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 rounded-xl">
            <CheckCircle className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Normal Load</p>
            <p className="text-2xl font-bold text-blue-600">{stats.normalCount}</p>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-green-100 rounded-xl">
            <TrendingDown className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Under-utilized</p>
            <p className="text-2xl font-bold text-green-600">{stats.underCount}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// LEGEND COMPONENT
// ==========================================

const Legend = () => (
  <div className="flex items-center gap-4 text-xs mb-4">
    <span className="text-gray-500">Utilisation:</span>
    <div className="flex items-center gap-1">
      <div className="w-4 h-4 rounded bg-red-500" />
      <span>Over (&gt;100%)</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-4 h-4 rounded bg-amber-400" />
      <span>High (80-100%)</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-4 h-4 rounded bg-blue-400" />
      <span>Normal (50-80%)</span>
    </div>
    <div className="flex items-center gap-1">
      <div className="w-4 h-4 rounded bg-green-400" />
      <span>Under (&lt;50%)</span>
    </div>
  </div>
);

// ==========================================
// MAIN PAGE COMPONENT
// ==========================================

const ResourceUtilisationPage = () => {
  const { tenantId: authTenantId } = useAuth();
  // TENANT ISOLATION: Use impersonated tenant if sysadmin is impersonating
  const tenantId = localStorage.getItem('impersonated_tenant_id') || authTenantId;
  const [loading, setLoading] = useState(true);
  const [heatmapData, setHeatmapData] = useState([]);
  const [months, setMonths] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [filter, setFilter] = useState('all'); // all, role, team
  const [displayCurrency, setDisplayCurrency] = useState('USD');
  
  // Date range state
  const [startMonth, setStartMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [endMonth, setEndMonth] = useState(() => {
    const now = new Date();
    now.setMonth(now.getMonth() + 5);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [heatmapRes, conflictsRes] = await Promise.all([
        api.getUtilisationHeatmap(
          tenantId, 
          startMonth, 
          endMonth,
          filter === 'all' ? null : filter
        ),
        api.getResourceConflicts(tenantId, startMonth, endMonth)
      ]);
      setHeatmapData(heatmapRes.data.heatmap || []);
      setMonths(heatmapRes.data.months || []);
      setConflicts(conflictsRes.data.conflicts || []);
    } catch (err) {
      console.error('Failed to load utilisation data:', err);
      toast.error('Failed to load utilisation data');
    } finally {
      setLoading(false);
    }
  }, [tenantId, startMonth, endMonth, filter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const shiftRange = (direction) => {
    const start = new Date(startMonth + '-01');
    const end = new Date(endMonth + '-01');
    const shift = direction === 'prev' ? -3 : 3;
    
    start.setMonth(start.getMonth() + shift);
    end.setMonth(end.getMonth() + shift);
    
    setStartMonth(`${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`);
    setEndMonth(`${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}`);
  };

  const filteredData = useMemo(() => {
    if (filter === 'all') return heatmapData;
    return heatmapData.filter(d => d.type === filter);
  }, [heatmapData, filter]);
  
  // Conflict stats
  const conflictStats = useMemo(() => ({
    total: conflicts.length,
    high: conflicts.filter(c => c.severity === 'high').length
  }), [conflicts]);

  return (
    <PageErrorBoundary>
      <div className="min-h-screen bg-gray-50 p-6" data-testid="resource-utilisation-page">
        {/* Back Button */}
        <BackButton to="/resources" label="Back to Resources" className="mb-4" />

        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Resource Utilisation</h1>
            <p className="text-gray-500">Heatmap view of resource allocation across time</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Quick Currency Switch */}
            <QuickCurrencySwitch 
              currency={displayCurrency} 
              onCurrencyChange={setDisplayCurrency} 
            />
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
        
        {/* Conflict Alert Banner */}
        {conflictStats.total > 0 && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3" data-testid="conflict-summary-banner">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-700">
              <span className="font-medium">{conflictStats.total} conflict{conflictStats.total > 1 ? 's' : ''}</span> detected in this time range 
              ({conflictStats.high} high severity)
            </p>
          </div>
        )}

        {/* Summary Stats */}
        <SummaryStats data={filteredData} months={months} />

        {/* Heatmap Card */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          {/* Toolbar */}
          <div className="px-4 py-3 border-b flex items-center justify-between bg-gray-50">
            <div className="flex items-center gap-4">
              {/* Filter */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  className="text-sm border-0 bg-transparent focus:ring-0 text-gray-700"
                  data-testid="filter-select"
                >
                  <option value="all">All Resources</option>
                  <option value="role">Roles Only</option>
                  <option value="team">Teams Only</option>
                </select>
              </div>
            </div>

            {/* Date Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => shiftRange('prev')}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="prev-months-btn"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2 px-3 py-1 bg-white border rounded-lg">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium">
                  {new Date(startMonth + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  {' - '}
                  {new Date(endMonth + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </span>
              </div>
              <button
                onClick={() => shiftRange('next')}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                data-testid="next-months-btn"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Legend */}
          <div className="px-4 py-2 border-b bg-gray-50">
            <Legend />
          </div>

          {/* Heatmap */}
          <WidgetErrorBoundary widgetName="Utilisation Heatmap">
            {loading ? (
              <div className="p-6 space-y-4">
                <SkeletonCard />
                <SkeletonCard />
                <SkeletonCard />
              </div>
            ) : (
              <div className="p-4">
                <HeatmapGrid data={filteredData} months={months} conflicts={conflicts} />
              </div>
            )}
          </WidgetErrorBoundary>
        </div>
      </div>
    </PageErrorBoundary>
  );
};

export default ResourceUtilisationPage;
